import { useEffect, useMemo, useState } from "react";
import { useDeleteAsk, deletionRequested } from "@/lib/deleteApproval";
import {
  listExpenses, createExpense, updateExpense, deleteExpense, expenseCategories,
  listCapex, capexCategories, createCapex, updateCapex, deleteCapex,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { atLeast, canWrite } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DatePicker } from "@/components/DatePicker";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, Trash2, Search, Pencil, ChevronLeft, ChevronRight, ReceiptText, Landmark,
} from "lucide-react";
import { numberLocale } from "@/lib/money";

const fmtINR = (n) => new Intl.NumberFormat(numberLocale(), { maximumFractionDigits: 0 }).format(n || 0);
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const CATEGORY_TINTS = {
  "Rent": "text-blue-400",
  "Utilities": "text-cyan-400",
  "Salaries": "text-violet-400",
  "Ad Spend": "text-rose-400",
  "Marketing": "text-pink-400",
  "Equipment": "text-amber-400",
  "Software": "text-emerald-400",
  "Maintenance": "text-orange-400",
  "Supplies": "text-lime-400",
  "Refunds": "text-orange-300",
  "Other": "text-zinc-400",
};

export default function Expenses() {
  const askDelete = useDeleteAsk();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState(() => new URLSearchParams(window.location.search).get("q") || "");
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);

  const refresh = () => listExpenses(year, month).then(setRows);
  useEffect(() => { refresh(); }, [year, month]);
  useEffect(() => { expenseCategories().then((d) => setCats(d.categories || [])); }, []);

  const filtered = useMemo(() => {
    if (!q) return rows;
    const s = q.toLowerCase();
    return rows.filter((r) =>
      [r.category, r.vendor, r.notes].some((v) => (v || "").toString().toLowerCase().includes(s))
    );
  }, [rows, q]);

  const totals = useMemo(() => {
    const total = (filtered || []).reduce((a, r) => a + (r.amount || 0), 0);
    const byCat = {};
    (filtered || []).forEach((r) => {
      byCat[r.category] = (byCat[r.category] || 0) + (r.amount || 0);
    });
    return { total, byCat };
  }, [filtered]);

  const shift = (delta) => {
    let m = month + delta, y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setMonth(m); setYear(y);
  };

  const remove = async (id) => {
    const reason = askDelete("this expense", "Delete this expense?");
    if (reason === null) return;
    try { if (deletionRequested(await deleteExpense(id, reason))) return; }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete it"); return; }
    toast.success("Expense deleted");
    refresh();
  };

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">Gym operating expenses</div>
          <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">Expenses · {MONTHS[month-1]} {year}</h2>
          <p className="text-sm text-zinc-400 mt-1">Track every cost — rent, utilities, salaries, ad spend, equipment, marketing.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 card-surface p-1 rounded-full">
            <Button variant="ghost" size="sm" onClick={() => shift(-1)} className="rounded-full h-8 w-8 p-0" data-testid="expenses-prev-month">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="px-3 text-sm font-medium tabular-nums" data-testid="expenses-current-period">{MONTHS[month-1]} {year}</div>
            <Button variant="ghost" size="sm" onClick={() => shift(1)} className="rounded-full h-8 w-8 p-0" data-testid="expenses-next-month">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <div className="relative w-full sm:w-auto">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="pl-9 bg-[#0a0a0a] border-[#262626] w-full sm:w-48" data-testid="expenses-search" />
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger data-testid="expenses-dialog-trigger-105" asChild>
              <Button className="bg-blue-600 hover:bg-blue-500 rounded-full px-5" data-testid="new-expense-btn" data-write>
                <Plus className="h-4 w-4 mr-1.5" /> New expense
              </Button>
            </DialogTrigger>
            <ExpenseDialog mode="create" cats={cats} onDone={() => { setOpen(false); refresh(); }} />
          </Dialog>
        </div>
      </div>

      {/* Totals */}
      <div className="card-surface p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="eyebrow">Total spend this month</div>
            <div className="font-display text-3xl sm:text-5xl font-extrabold mt-2 tabular-nums text-rose-400">
              {window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{fmtINR(totals.total)}
            </div>
            <div className="text-xs text-zinc-500 mt-1">{filtered.length} entries</div>
          </div>
          <ReceiptText className="h-16 w-16 text-rose-500/20" strokeWidth={1.2} />
        </div>
        {Object.keys(totals.byCat).length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-6">
            {Object.entries(totals.byCat)
              .sort((a, b) => b[1] - a[1])
              .map(([cat, amt]) => (
                <div key={cat} className="rounded-lg border border-[#1f1f1f] p-3 bg-[#0d0d0d]">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">{cat}</div>
                  <div className={`font-display text-xl font-extrabold mt-1 tabular-nums ${CATEGORY_TINTS[cat] || "text-zinc-300"}`}>
                    {window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{fmtINR(amt)}
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="card-surface overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.2em] text-zinc-500 border-b border-[#1f1f1f] bg-[#0d0d0d]">
                <th className="px-4 py-3 font-medium">Date</th>
                <th className="px-4 py-3 font-medium">Category</th>
                <th className="px-4 py-3 font-medium">Vendor</th>
                <th className="px-4 py-3 font-medium">Notes</th>
                <th className="px-4 py-3 font-medium text-right">Amount</th>
                <th className="px-4 py-3 font-medium">Logged by</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {(filtered || []).map((r) => (
                <tr key={r.id} className="border-b border-[#1a1a1a] last:border-0 hover:bg-[#161616]">
                  <td className="px-4 py-3 text-zinc-400 text-xs whitespace-nowrap">
                    {new Date(r.date).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium border border-[#262626] bg-[#0a0a0a] ${CATEGORY_TINTS[r.category] || "text-zinc-300"}`}>
                      {r.category}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-zinc-300 text-xs">{r.vendor || "—"}</td>
                  <td className="px-4 py-3 text-zinc-400 text-xs max-w-[16rem] truncate" title={r.notes}>
                    {r.notes || <span className="text-zinc-700">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-rose-300">{window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{fmtINR(r.amount)}</td>
                  <td className="px-4 py-3 text-zinc-400 text-xs">{r.created_by_name || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={() => setEdit(r)} className="text-zinc-500 hover:text-blue-400 p-1" data-testid={`edit-expense-${r.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => remove(r.id)} className="text-zinc-500 hover:text-rose-400 p-1" data-testid={`delete-expense-${r.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-14 text-center text-sm text-zinc-500">
                    No expenses logged for {MONTHS[month-1]} {year} yet — click New expense to log the first.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        {edit && <ExpenseDialog mode="edit" cats={cats} initial={edit} onDone={() => { setEdit(null); refresh(); }} />}
      </Dialog>

      <CapexSection year={year} month={month} />
    </div>
  );
}

// ---------------- Capital expenses (depreciated over months) ----------------
const pad2 = (n) => String(n).padStart(2, "0");
const ymLabel = (ym) => (ym ? `${MONTHS[Number(ym.slice(5, 7)) - 1]} ${ym.slice(0, 4)}` : "—");

function CapexSection({ year, month }) {
  const askDelete = useDeleteAsk();
  const { user } = useAuth();
  const canEdit = canWrite(user) && (atLeast(user?.role, "manager") || atLeast(user?.effective_role, "manager"));
  const ym = `${year}-${pad2(month)}`;
  const [data, setData] = useState(null);
  const [standard, setStandard] = useState([]);
  const [dialog, setDialog] = useState(null); // null | "new" | item
  const load = () => listCapex(ym).then(setData).catch(() => setData({ items: [], totals: {} }));
  useEffect(() => { load(); }, [ym]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { capexCategories().then((d) => setStandard(d.standard || [])).catch(() => {}); }, []);
  const sym = window.__ACTIVE_CURRENCY_SYMBOL__ || "";
  const remove = async (item) => {
    const reason = askDelete(`"${item.name}"`, `Delete "${item.name}"? Its depreciation is removed from every month.`);
    if (reason === null) return;
    try { if (deletionRequested(await deleteCapex(item.id, reason))) return; }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete it"); return; }
    toast.success("Capital expense deleted");
    load();
  };
  if (!data) return null;
  const items = data.items || [];
  return (
    <section className="space-y-4" data-testid="capex-section">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">Capital expenses</div>
          <h3 className="font-display text-2xl font-extrabold mt-1 tracking-tight">Equipment & fit-out · depreciation</h3>
          <p className="text-sm text-zinc-400 mt-1">
            Big one-off purchases are spread evenly over their depreciation period. Each month gets an equal
            <span className="text-zinc-200"> Depreciation </span>charge in the P&amp;L instead of the full cost landing in one month.
          </p>
        </div>
        {canEdit && (
          <Button onClick={() => setDialog("new")} className="bg-amber-600 hover:bg-amber-500 rounded-full px-5" data-testid="new-capex-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Add capital expense
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="card-surface p-4">
          <div className="eyebrow">Depreciation · {MONTHS[month - 1]} {year}</div>
          <div className="font-display text-3xl font-extrabold mt-1 tabular-nums text-amber-300" data-testid="capex-month-charge">{sym}{fmtINR(data.totals?.this_month_charge)}</div>
          <div className="text-[11px] text-zinc-500 mt-1">Counted in this month&apos;s expenses &amp; profit</div>
        </div>
        <div className="card-surface p-4">
          <div className="eyebrow">Total capital cost</div>
          <div className="font-display text-3xl font-extrabold mt-1 tabular-nums">{sym}{fmtINR(data.totals?.cost)}</div>
          <div className="text-[11px] text-zinc-500 mt-1">{items.length} item{items.length === 1 ? "" : "s"}</div>
        </div>
        <div className="card-surface p-4">
          <div className="eyebrow">Book value at end of {MONTHS[month - 1]}</div>
          <div className="font-display text-3xl font-extrabold mt-1 tabular-nums text-emerald-300">{sym}{fmtINR(data.totals?.book_value)}</div>
          <div className="text-[11px] text-zinc-500 mt-1">Cost not yet depreciated</div>
        </div>
      </div>

      <div className="card-surface overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.2em] text-zinc-500 border-b border-[#1f1f1f] bg-[#0d0d0d]">
                <th className="px-4 py-3 font-medium">Item</th>
                <th className="px-4 py-3 font-medium">Bought</th>
                <th className="px-4 py-3 font-medium text-right">Cost</th>
                <th className="px-4 py-3 font-medium">Depreciation</th>
                <th className="px-4 py-3 font-medium text-right">Per month</th>
                <th className="px-4 py-3 font-medium">Progress</th>
                <th className="px-4 py-3 font-medium text-right">Book value</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => {
                const pct = r.months ? Math.round((r.months_done / r.months) * 100) : 0;
                return (
                  <tr key={r.id} className="border-b border-[#1a1a1a] last:border-0 hover:bg-[#161616]" data-testid={`capex-row-${r.id}`}>
                    <td className="px-4 py-3">
                      <div className="text-zinc-100 font-medium">{r.name}</div>
                      <div className="text-[11px] text-zinc-500">{r.category}{r.vendor ? ` · ${r.vendor}` : ""}</div>
                    </td>
                    <td className="px-4 py-3 text-zinc-400 text-xs whitespace-nowrap">{new Date(r.purchase_date).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-right font-mono">{sym}{fmtINR(r.amount)}</td>
                    <td className="px-4 py-3 text-xs text-zinc-400 whitespace-nowrap">{r.months} months<br /><span className="text-zinc-500">{ymLabel(r.start_month)} → {ymLabel(r.end_month)}</span></td>
                    <td className="px-4 py-3 text-right font-mono text-amber-300">{sym}{fmtINR(r.monthly_charge)}</td>
                    <td className="px-4 py-3 min-w-[9rem]">
                      <div className="h-1.5 rounded-full bg-[#1f1f1f] overflow-hidden"><div className="h-full bg-amber-500/70" style={{ width: `${pct}%` }} /></div>
                      <div className="text-[11px] text-zinc-500 mt-1 tabular-nums">{r.months_done} of {r.months} months</div>
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-emerald-300">{sym}{fmtINR(r.book_value)}</td>
                    <td className="px-4 py-3 text-right">
                      {canEdit && (
                        <div className="flex items-center gap-1 justify-end">
                          <button onClick={() => setDialog(r)} className="text-zinc-500 hover:text-blue-400 p-1" data-testid={`edit-capex-${r.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                          <button onClick={() => remove(r)} className="text-zinc-500 hover:text-rose-400 p-1" data-testid={`delete-capex-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {items.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-zinc-500">
                  <Landmark className="h-6 w-6 mx-auto mb-2 text-zinc-700" />
                  No capital expenses yet. Add equipment, fit-out or other big purchases to spread their cost over time.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!dialog} onOpenChange={(v) => !v && setDialog(null)}>
        {dialog && <CapexDialog initial={dialog === "new" ? null : dialog} standard={standard} onDone={() => { setDialog(null); load(); }} />}
      </Dialog>
    </section>
  );
}

function CapexDialog({ initial, standard, onDone }) {
  const [f, setF] = useState(() => ({
    name: "", category: "Strength & other gym equipment", amount: "", purchase_date: new Date().toISOString().slice(0, 10),
    months: 36, start_month: "", vendor: "", notes: "", ...(initial || {}),
  }));
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const std = (standard || []).find((c) => c.name === f.category);
  // Picking a category applies its standard period (blank for "Other").
  const pickCategory = (name) => {
    const c = (standard || []).find((x) => x.name === name);
    setF((s) => ({ ...s, category: name, months: c?.months ?? "" }));
  };
  const offStandard = std?.months && Number(f.months) !== std.months;
  const amount = Number(f.amount) || 0;
  const months = Number(f.months) || 0;
  const perMonth = amount > 0 && months > 0 ? amount / months : 0;
  const sym = window.__ACTIVE_CURRENCY_SYMBOL__ || "";
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { ...f, amount, months, start_month: f.start_month || null };
    try {
      if (initial) await updateCapex(initial.id, payload);
      else await createCapex(payload);
      toast.success(initial ? "Capital expense updated" : "Capital expense added");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };
  return (
    <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100 max-w-lg">
      <DialogHeader><DialogTitle className="font-display text-xl">{initial ? "Edit capital expense" : "Add capital expense"}</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="space-y-4" data-testid="capex-form">
        <F label="Item" required><Input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. 4 × commercial treadmills" className="bg-[#0a0a0a] border-[#262626]" data-testid="capex-name" /></F>
        <div className="grid grid-cols-2 gap-3">
          <F label="Category">
            <Select value={f.category} onValueChange={pickCategory}>
              <SelectTrigger className="bg-[#0a0a0a] border-[#262626]" data-testid="capex-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(standard.length ? standard : [{ name: f.category }]).map((c) => (
                  <SelectItem key={c.name} value={c.name}>{c.name}{c.months ? ` · ${c.months / 12} yr` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </F>
          <F label="Total cost" required><Input type="number" min="0" step="0.01" value={f.amount} onChange={(e) => set("amount", e.target.value)} className="bg-[#0a0a0a] border-[#262626] font-mono" data-testid="capex-amount" /></F>
          <F label="Purchase date" required><DatePicker value={(f.purchase_date || "").slice(0, 10)} onChange={(e) => set("purchase_date", e.target.value)} data-testid="capex-date" /></F>
          <F label="Depreciation period (months)" required><Input type="number" min="1" max="240" value={f.months} onChange={(e) => set("months", e.target.value)} className="bg-[#0a0a0a] border-[#262626] font-mono" data-testid="capex-months" /></F>
          <F label="Start charging from (optional)"><Input type="month" value={f.start_month || ""} onChange={(e) => set("start_month", e.target.value)} className="bg-[#0a0a0a] border-[#262626]" data-testid="capex-start" /></F>
          <F label="Vendor"><Input value={f.vendor || ""} onChange={(e) => set("vendor", e.target.value)} className="bg-[#0a0a0a] border-[#262626]" /></F>
        </div>
        {std?.covers && (
          <div className="text-[11px] text-zinc-500 -mt-1" data-testid="capex-covers">
            <span className="text-zinc-400">{std.name}{std.months ? ` (${std.months} months)` : ""}:</span> {std.covers}
          </div>
        )}
        {offStandard && (
          <div className="text-[11px] text-amber-300/90" data-testid="capex-off-standard">
            Standard for {std.name} is {std.months} months — you&apos;ve set {f.months}. Add a note explaining why.
          </div>
        )}
        <F label="Notes"><Textarea rows={2} value={f.notes || ""} onChange={(e) => set("notes", e.target.value)} className="bg-[#0a0a0a] border-[#262626]" /></F>
        {perMonth > 0 && (
          <div className="rounded-lg border border-amber-500/20 bg-amber-500/[0.04] px-3 py-2 text-xs text-zinc-300" data-testid="capex-preview">
            {sym}{fmtINR(amount)} over {months} months = <span className="font-mono text-amber-300">{sym}{new Intl.NumberFormat(numberLocale(), { maximumFractionDigits: 2 }).format(perMonth)}</span> charged every month
            {` from ${f.start_month ? ymLabel(f.start_month) : ymLabel((f.purchase_date || "").slice(0, 7))}`}.
          </div>
        )}
        <DialogFooter>
          <Button type="submit" disabled={busy} className="bg-amber-600 hover:bg-amber-500 rounded-full px-6" data-testid="capex-save">{busy ? "Saving…" : initial ? "Save changes" : "Add"}</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function ExpenseDialog({ mode, initial, cats, onDone }) {
  const [f, setF] = useState(() => ({
    category: cats[0] || "Rent",
    amount: 0,
    date: new Date().toISOString().slice(0, 10),
    vendor: "",
    notes: "",
    ...(initial ? { ...initial, date: (initial.date || "").slice(0, 10) } : {}),
  }));
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.category) return toast.error("Category required");
    if (!f.amount || Number(f.amount) <= 0) return toast.error("Amount must be greater than 0");
    setBusy(true);
    try {
      const payload = {
        category: f.category,
        amount: Number(f.amount),
        date: f.date,
        vendor: f.vendor || "",
        notes: f.notes || "",
      };
      if (mode === "create") await createExpense(payload);
      else await updateExpense(initial.id, payload);
      toast.success("Saved");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100 max-w-lg" data-testid="expense-dialog">
      <DialogHeader>
        <DialogTitle className="font-display text-xl">
          {mode === "create" ? "New expense" : `Edit expense`}
        </DialogTitle>
      </DialogHeader>
      <form data-testid="expenses-form-250" onSubmit={submit} className="space-y-4 mt-2">
        <div className="grid grid-cols-2 gap-3">
          <F label="Category" required>
            <Select data-testid="expenses-select-253" value={f.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger className="bg-[#0a0a0a] border-[#262626]" data-testid="expense-category-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100">
                {(cats || []).map((c) => (
                  <SelectItem data-testid="expenses-select-item-259" key={c} value={c} className="focus:bg-[#1a1a1a]">{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </F>
          <F label="Date" required>
            <DatePicker value={f.date} onChange={(e) => set("date", e.target.value)} data-testid="expense-date-input" />
          </F>
          <F label={`Amount (${window.__ACTIVE_CURRENCY_SYMBOL__ || ""})`} required>
            <Input type="number" step="0.01" value={f.amount} onChange={(e) => set("amount", e.target.value)} className="bg-[#0a0a0a] border-[#262626] font-mono" data-testid="expense-amount-input" />
          </F>
          <F label="Vendor">
            <Input value={f.vendor} onChange={(e) => set("vendor", e.target.value)} className="bg-[#0a0a0a] border-[#262626]" data-testid="expense-vendor-input" placeholder="Optional" />
          </F>
        </div>
        <F label="Notes">
          <Textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="bg-[#0a0a0a] border-[#262626]" data-testid="expense-notes-input" placeholder="Optional context" />
        </F>
        <DialogFooter>
          <Button type="submit" disabled={busy} className="bg-blue-600 hover:bg-blue-500 rounded-full" data-testid="expense-submit-btn">
            {busy ? "Saving…" : mode === "create" ? "Log expense" : "Save"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function F({ label, required, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
        {label}{required && <span className="text-rose-400 ml-1">*</span>}
      </Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
